import React, {
  createContext,
  useContext,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useWidgetContext } from "./WidgetProvider";
import { useStakes } from "../hooks/useStakes";
import { useConfirm } from "./ConfirmBannerProvider";
import { useError } from "./ErrorBannerProvider";
import { useI18n } from "./I18nProvider";
import { BetSlipMode, EventStatus, StateActionTypes } from "../types/enums";
import { fetchEventsByIds } from "../shared/utils/bet-slip";

interface BetSlipContextType {
  config: IWidgetConfig;
  betSlipState: IBetSlipState;
  stakes: { [key: string]: number };
  globalStake: number;
  selectedBonus: IBetSlipBonus | null;
  addSelection: (payload: IAddSelectionPayload) => void;
  removeSelection: (selection: IBetSlipSelection) => void;
  clearAllSelections: () => void;
  onChangeMode: (mode: BetSlipMode) => void;
  updateStake: (selectionKey: string, stake: number) => void;
  updateGlobalStake: (stake: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | undefined) => void;
  selectBonus: (bonus: IBetSlipBonus | null) => void;
  onOrderExecuted?: (response: ExecuteOrderResponse) => void;
  onNavigate: (route: string) => void;
}

const BetSlipContext = createContext<BetSlipContextType | undefined>(undefined);

interface BetSlipProviderProps {
  children: ReactNode;
  onStateChange: (state: Partial<IWidgetState>) => void;
  onError?: (error: any) => void;
  onOrderExecuted?: (response: ExecuteOrderResponse) => void;
  onNavigate: (route: string) => void;
}

export const BetSlipProvider: React.FC<BetSlipProviderProps> = ({
  children,
  onStateChange,
  onError,
  onOrderExecuted,
  onNavigate,
}) => {
  const { dispatch, config, betSlipState, error, placeBetInProgressRef } = useWidgetContext();
  const { showConfirm } = useConfirm();
  const { showError } = useError();
  const { t } = useI18n();

  const {
    stakes,
    globalStake,
    updateStake: updateStakeHook,
    updateGlobalStake: updateGlobalStakeHook,
    resetStakes,
  } = useStakes({
    selections: betSlipState.selections,
    mode: betSlipState.mode,
  });

  const [selectedBonus, setSelectedBonus] =
    React.useState<IBetSlipBonus | null>(null);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ config, betSlipState });
    }
  }, [betSlipState, config, onStateChange]);

  useEffect(() => {
    if (onError) {
      onError(error);
    }
  }, [error, onError]);

  // One-time resync of whatever was restored from localStorage: the host may
  // never re-send an update for an event that closed while the widget wasn't
  // mounted, so re-check every event currently held — regular selections,
  // Bet Builder totalOdds, AND Bet Builder bets directly (a lone, not-yet-
  // combined BB leg may have no totalOdds entry yet) — against the live feed.
  // Deliberately runs once (empty deps) — this isn't a recurring poll, only
  // a correction for whatever localStorage restored at load time.
  useEffect(() => {
    const eventIds = new Set<number>();
    betSlipState.selections.forEach((s) => eventIds.add(Number(s.eventId)));
    betSlipState.betBuilderState?.totalOdds?.forEach((to) =>
      eventIds.add(Number(to.eventId)),
    );
    betSlipState.betBuilderState?.bets?.forEach((bet) =>
      eventIds.add(Number(bet.eventId)),
    );
    if (eventIds.size === 0) return;

    let cancelled = false;
    fetchEventsByIds([...eventIds])
      .then((data) => {
        if (!cancelled && data.length > 0) {
          dispatch({
            type: StateActionTypes.UPDATE_ODDS_CHANGES,
            payload: { data },
          });
        }
      })
      .catch(() => {
        // best-effort resync — the push channel remains the primary path
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleAddSelection = (e: Event) => {
      const customEvent = e as CustomEvent<IAddSelectionPayload>;
      dispatch({
        type: StateActionTypes.ADD_SELECTION,
        payload: customEvent.detail,
      });
    };

    const handleRemoveSelection = (e: Event) => {
      const customEvent = e as CustomEvent<IBetSlipSelection>;
      dispatch({
        type: StateActionTypes.REMOVE_SELECTION,
        payload: customEvent.detail,
      });
    };

    // Normalize odds data from widget API to match expected IEvent structure
    const normalizeOddsData = (events: any[]): IEvent[] => {
      return events.map((event) => {
        // If event already has 'bts' property, it's already normalized (from internal API)
        if (event.bts) {
          return event;
        }

        // If event has 'odds' property, normalize it (from widget API)
        if (event.odds) {
          return {
            ...event,
            st: event.st || EventStatus.Prematch, // Event status - default to 0 (active)
            bts: Object.entries(event.odds).map(
              ([betTypeId, betTypeData]: [string, any]) => ({
                id: parseInt(betTypeId),
                n: betTypeData.name || "Bet Type", // Bet type name
                o: 1, // Order - default to 1
                s: 0, // Bet type status - default to 0 (active)
                odds: Object.entries(betTypeData).flatMap(
                  ([line, lineData]: [string, any]) =>
                    Object.entries(lineData).map(
                      ([oddName, oddData]: [string, any]) => ({
                        id: oddData.id,
                        n: oddData.n,
                        l: oddData.l,
                        or: oddData.or,
                        oc: oddData.oc,
                        p: oddData.p,
                        s: oddData.s,
                      }),
                    ),
                ),
              }),
            ),
          };
        }

        // Return as-is if no normalization needed
        return event;
      });
    };

    const handleDataChange = (e: Event) => {
      if (placeBetInProgressRef.current) return;
      const customEvent = e as CustomEvent<{ data: IEvent[] }>;
      const normalizedData = {
        ...customEvent.detail,
        data: normalizeOddsData(customEvent.detail.data),
      };
      dispatch({
        type: StateActionTypes.UPDATE_ODDS_CHANGES,
        payload: normalizedData,
      });
    };

    const handleUpdateConfig = (e: Event) => {
      const customEvent = e as CustomEvent<IWidgetConfig>;
      dispatch({
        type: StateActionTypes.UPDATE_CONFIG,
        payload: customEvent.detail,
      });
    };

    const handleUpdateBetBuilderState = (e: Event) => {
      const customEvent = e as CustomEvent<IBetBuilderState>;
      dispatch({
        type: StateActionTypes.UPDATE_BET_BUILDER_STATE,
        payload: customEvent.detail,
      });
    };

    const handleClearAll = () => {
      dispatch({ type: StateActionTypes.CLEAR_ALL_SELECTIONS });
    };

    const handleRecalculate = (e: Event) => {
      const customEvent = e as CustomEvent<IBetSlipSelection[]>;
      dispatch({
        type: StateActionTypes.BB_RECALCULATE,
        payload: customEvent.detail,
      });
    };

    const handleAcceptOddsChanges = (e: Event) => {
      const customEvent = e as CustomEvent<boolean>;
      dispatch({
        type: StateActionTypes.ACCEPT_ODDS_CHANGES,
        payload: customEvent.detail,
      });
    };

    const handleToggleAcceptOddsChanges = (e: Event) => {
      const customEvent = e as CustomEvent<boolean>;
      dispatch({
        type: StateActionTypes.TOGGLE_ACCEPT_ODDS_CHANGES,
        payload: customEvent.detail,
      });
    };

    const handleSetSource = (e: Event) => {
      const customEvent = e as CustomEvent<{ source: string | null; sourceRef: string | null }>;
      dispatch({
        type: StateActionTypes.SET_SOURCE,
        payload: customEvent.detail,
      });
    };

    const handleUpdateSingleStake = (e: Event) => {
      const customEvent = e as CustomEvent<{
        selectionKey: string;
        stake: number;
      }>;
      updateStakeHook(
        customEvent.detail.selectionKey,
        customEvent.detail.stake,
      );
    };

    const handleUpdateGlobalStake = (e: Event) => {
      const customEvent = e as CustomEvent<number>;
      updateGlobalStakeHook(customEvent.detail);
    };

    const handleChangeMode = (e: Event) => {
      const customEvent = e as CustomEvent<BetSlipMode>;
      dispatch({
        type: StateActionTypes.SET_MODE,
        payload: customEvent.detail,
      });
    };

    const handleShowBBConfirm = (e: Event) => {
      const customEvent = e as CustomEvent<{
        pendingSelection: IBetSlipSelection;
      }>;
      const { pendingSelection } = customEvent.detail;

      showConfirm(
        "This selection isn't part of Bet Builder. To continue, choose one of the following:",
        "Keep Bet Builder",
        "Switch To This Selection",
        () => {
          // Confirm - Keep Bet Builder (cancel the action)
          dispatch({ type: StateActionTypes.CANCEL_BET_BUILDER_REMOVAL });
        },
        () => {
          // Cancel - Switch to new selection (confirm the removal)
          dispatch({
            type: StateActionTypes.CONFIRM_BET_BUILDER_REMOVAL,
            payload: pendingSelection,
          });
        },
      );
    };

    const handleBetslipSelectionsLimit = () => {
      showError(t("BetslipWidgetSelectionsLimit", { limit: config.brand.selectionsLimit }));
    };

    document.addEventListener(
      StateActionTypes.ADD_SELECTION,
      handleAddSelection,
    );
    document.addEventListener(
      StateActionTypes.REMOVE_SELECTION,
      handleRemoveSelection,
    );
    document.addEventListener(
      StateActionTypes.CLEAR_ALL_SELECTIONS,
      handleClearAll,
    );
    document.addEventListener(
      StateActionTypes.BB_RECALCULATE,
      handleRecalculate,
    );
    document.addEventListener(
      StateActionTypes.ACCEPT_ODDS_CHANGES,
      handleAcceptOddsChanges,
    );
    document.addEventListener(
      StateActionTypes.TOGGLE_ACCEPT_ODDS_CHANGES,
      handleToggleAcceptOddsChanges,
    );
    document.addEventListener(StateActionTypes.SET_SOURCE, handleSetSource);
    document.addEventListener(
      StateActionTypes.UPDATE_CONFIG,
      handleUpdateConfig,
    );
    document.addEventListener(
      StateActionTypes.UPDATE_ODDS_CHANGES,
      handleDataChange,
    );
    document.addEventListener(
      StateActionTypes.UPDATE_BET_BUILDER_STATE,
      handleUpdateBetBuilderState,
    );
    document.addEventListener(
      StateActionTypes.UPDATE_SINGLE_STAKE,
      handleUpdateSingleStake,
    );
    document.addEventListener(
      StateActionTypes.UPDATE_GLOBAL_STAKE,
      handleUpdateGlobalStake,
    );
    document.addEventListener(StateActionTypes.SET_MODE, handleChangeMode);
    document.addEventListener(
      StateActionTypes.SHOW_BB_CONFIRM,
      handleShowBBConfirm,
    );
    document.addEventListener(
      StateActionTypes.BETSLIP_SELECTIONS_LIMIT,
      handleBetslipSelectionsLimit,
    );

    return () => {
      document.removeEventListener(
        StateActionTypes.ADD_SELECTION,
        handleAddSelection,
      );
      document.removeEventListener(
        StateActionTypes.REMOVE_SELECTION,
        handleRemoveSelection,
      );
      document.removeEventListener(
        StateActionTypes.CLEAR_ALL_SELECTIONS,
        handleClearAll,
      );
      document.removeEventListener(
        StateActionTypes.BB_RECALCULATE,
        handleRecalculate,
      );
      document.removeEventListener(
        StateActionTypes.ACCEPT_ODDS_CHANGES,
        handleAcceptOddsChanges,
      );
      document.removeEventListener(
        StateActionTypes.TOGGLE_ACCEPT_ODDS_CHANGES,
        handleToggleAcceptOddsChanges,
      );
      document.removeEventListener(StateActionTypes.SET_SOURCE, handleSetSource);
      document.removeEventListener(
        StateActionTypes.UPDATE_CONFIG,
        handleUpdateConfig,
      );
      document.removeEventListener(
        StateActionTypes.UPDATE_ODDS_CHANGES,
        handleDataChange,
      );
      document.removeEventListener(
        StateActionTypes.UPDATE_BET_BUILDER_STATE,
        handleUpdateBetBuilderState,
      );
      document.removeEventListener(
        StateActionTypes.UPDATE_SINGLE_STAKE,
        handleUpdateSingleStake,
      );
      document.removeEventListener(
        StateActionTypes.UPDATE_GLOBAL_STAKE,
        handleUpdateGlobalStake,
      );
      document.removeEventListener(StateActionTypes.SET_MODE, handleChangeMode);
      document.removeEventListener(
        StateActionTypes.SHOW_BB_CONFIRM,
        handleShowBBConfirm,
      );
      document.removeEventListener(
        StateActionTypes.BETSLIP_SELECTIONS_LIMIT,
        handleBetslipSelectionsLimit,
      );
    };
  }, [dispatch, updateStakeHook, updateGlobalStakeHook, showConfirm, showError, t, config.brand.selectionsLimit]);

  const addSelection = useCallback(
    (payload: IAddSelectionPayload) => {
      dispatch({ type: StateActionTypes.ADD_SELECTION, payload });
    },
    [dispatch],
  );

  const removeSelection = useCallback(
    (selection: IBetSlipSelection) => {
      dispatch({ type: StateActionTypes.REMOVE_SELECTION, payload: selection });
    },
    [dispatch],
  );

  const clearAllSelections = useCallback(() => {
    dispatch({ type: StateActionTypes.CLEAR_ALL_SELECTIONS });
    resetStakes();
    setSelectedBonus(null);
  }, [resetStakes, setSelectedBonus]);

  const onChangeMode = useCallback(
    (mode: BetSlipMode) => {
      dispatch({ type: StateActionTypes.SET_MODE, payload: mode });
      resetStakes();
      setSelectedBonus(null);
    },
    [resetStakes, setSelectedBonus],
  );

  const updateStake = useCallback(
    (selectionKey: string, stake: number) => {
      updateStakeHook(selectionKey, stake);
    },
    [updateStakeHook],
  );

  const updateGlobalStake = useCallback(
    (stake: number) => {
      updateGlobalStakeHook(stake);
    },
    [updateGlobalStakeHook],
  );

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: StateActionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error: string | undefined) => {
    dispatch({ type: StateActionTypes.SET_ERROR, payload: error });
  }, []);

  const selectBonus = useCallback((bonus: IBetSlipBonus | null) => {
    setSelectedBonus(bonus);
  }, []);

  const contextValue: BetSlipContextType = {
    config,
    betSlipState,
    stakes,
    globalStake,
    selectedBonus,
    addSelection,
    removeSelection,
    clearAllSelections,
    onChangeMode,
    updateStake,
    updateGlobalStake,
    setLoading,
    setError,
    selectBonus,
    onOrderExecuted,
    onNavigate
  };

  return (
    <BetSlipContext.Provider value={contextValue}>
      {children}
    </BetSlipContext.Provider>
  );
};

export const useBetSlip = (): BetSlipContextType => {
  const context = useContext(BetSlipContext);
  if (!context) {
    throw new Error("useBetSlipContext must be used within a BetSlipProvider");
  }
  return context;
};
